import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from 'environments/environment';

declare global {
  interface Window {
    appInsights?: ApplicationInsights;
    testAppInsightsException?: () => void;
  }
}

// Property/header names that must never leave the browser in telemetry payloads.
const SENSITIVE_KEYS = [
  'authorization', 'access_token', 'refresh_token', 'token', 'password',
  'ssn', 'sin', 'dob', 'email', 'phone', 'creditcard', 'card_number'
];

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights;
  private isInitialized = false;
  // Invite key stamped as customDimensions.invite_key on every envelope, once set.
  private currentInviteKey: string;

  constructor(private router: Router) { }

  public initialize(): void {
    if (this.isInitialized || !environment['app_insights_connection_string']) {
      return;
    }

    const angularPlugin = new AngularPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment['app_insights_connection_string'],
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router }
        },
        enableAutoRouteTracking: true,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        disableExceptionTracking: false,
        autoTrackPageVisitTime: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false
      }
    });

    this.appInsights.addTelemetryInitializer(this.scrubSensitiveData);
    this.appInsights.loadAppInsights();
    this.isInitialized = true;

    // Dev-only console inspection/validation hooks; never exposed in production builds.
    if (!environment.production) {
      window.appInsights = this.appInsights;
      window.testAppInsightsException = () => {
        throw new Error('App Insights test exception - triggered manually for validation');
      };
    }
  }

  public trackPageView(name?: string, uri?: string): void {
    if (!this.isInitialized) {
      return;
    }
    this.appInsights.trackPageView({ name, uri });
  }

  public trackEvent(name: string, properties?: { [key: string]: any }): void {
    if (!this.isInitialized) {
      return;
    }
    this.appInsights.trackEvent({ name }, this.redactProperties(properties));
  }

  public trackException(error: Error, severityLevel?: number): void {
    if (!this.isInitialized) {
      return;
    }
    this.appInsights.trackException({ exception: error, severityLevel });
  }

  // userId must be an internal opaque identifier (e.g. invite key) - never an email/name, to avoid logging PII.
  public setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.isInitialized || !userId) {
      return;
    }
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  public clearAuthenticatedUser(): void {
    if (!this.isInitialized) {
      return;
    }
    this.appInsights.clearAuthenticatedUserContext();
  }

  // Stamps invite_key as a custom property (customDimensions.invite_key) on all telemetry, without
  // touching session_Id/user_Id so AI's built-in Users/Sessions counters stay accurate.
  public setInviteKey(inviteKey: string): void {
    if (!inviteKey) {
      return;
    }
    this.currentInviteKey = inviteKey;
  }

  // Redacts sensitive keys/values before they are handed off to the SDK for sending.
  private scrubSensitiveData = (envelope: ITelemetryItem): boolean => {
    if (this.currentInviteKey) {
      envelope.data = envelope.data || {};
      envelope.data['invite_key'] = this.currentInviteKey;
    }
    this.redactObjectInPlace(envelope.baseData);
    this.redactObjectInPlace(envelope.data);
    return true;
  }

  private redactProperties(properties?: { [key: string]: any }): { [key: string]: any } {
    if (!properties) {
      return properties;
    }
    const clone = { ...properties };
    this.redactObjectInPlace(clone);
    return clone;
  }

  private redactObjectInPlace(obj: any): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }
    for (const key of Object.keys(obj)) {
      if (SENSITIVE_KEYS.some(sensitive => key.toLowerCase().includes(sensitive))) {
        obj[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'string') {
        obj[key] = this.stripTokensFromString(obj[key]);
      } else if (typeof obj[key] === 'object') {
        this.redactObjectInPlace(obj[key]);
      }
    }
  }

  // Strips bearer tokens and common token/PII query params that may end up in URLs or messages.
  private stripTokensFromString(value: string): string {
    return value
      .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]')
      .replace(/([?&](?:token|access_token|refresh_token|password)=)[^&\s]+/gi, '$1[REDACTED]');
  }
}
